import type { TicketListItem } from '../../api/types';

/**
 * Two fields the API puts on every list row that `TicketListItem` has not declared yet —
 * `child_count` and `parent_key`, added by the API at `aff85f6` as two more LATERAL aggregates on
 * the same page query.
 *
 * ⚠️ READ DEFENSIVELY, AND FROM HERE ONLY. `src/api/types.ts` is shared and being edited by other
 * agents right now; racing them for the same lines would produce a merge conflict in the one file
 * every view depends on. When the shared type declares these, this file keeps working and can be
 * deleted in one commit.
 *
 * ⭐⭐ WHY IT MATTERS THAT `null` IS A THIRD ANSWER. `APFS-441` has EIGHTEEN sub-tasks, and a board is
 * a bounded window that might hold two of them. "(2 subtasks)" next to a story Jira calls 18 is a
 * confident wrong number, which is worse than an error because nobody questions it.
 *   · a number → the API counted the whole project. Use it.
 *   · null     → the API did not say. Say "2 on this board", never "2".
 * ⛔ Absent is never coerced to 0: `child_count` is 0 for a ticket with no children, and that is a
 * different fact from "we were not told".
 */

function field(ticket: TicketListItem, key: string): unknown {
  return (ticket as unknown as Record<string, unknown>)[key];
}

export function childCountOf(ticket: TicketListItem): number | null {
  const value = field(ticket, 'child_count');
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  // ⚠️ BIGINT-adjacent values arrive as strings from this API often enough to check for one.
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

export function parentKeyOf(ticket: TicketListItem): string | null {
  const value = field(ticket, 'parent_key');
  return typeof value === 'string' && value.trim() ? value : null;
}

/**
 * How a swimlane header states its size.
 * ⭐ Three renderings for three different states of knowledge, and the caller cannot collapse them.
 */
export function describeSubtaskCount(
  ticket: TicketListItem,
  shownHere: number,
): { text: string; isExact: boolean } {
  const total = childCountOf(ticket);
  const word = (n: number) => (n === 1 ? 'subtask' : 'subtasks');

  if (total === null) return { text: `${shownHere} ${word(shownHere)} on this board`, isExact: false };
  if (total === shownHere) return { text: `${total} ${word(total)}`, isExact: true };
  return { text: `${total} ${word(total)} · ${shownHere} here`, isExact: true };
}
